import React, { useState, useContext, useMemo, useEffect } from 'react';

import DatePickerComponent from '../Components/DatePickerComponent';
import DateTimePickerComponent from '../Components/DateTimePickerComponent';
import SimpleSelect from '../Components/SimpleSelect';
import SimpleTextInput from '../Components/SimpleTextInput';
import KebabInput from '../Components/KebabInput';
import {
  VehicleModal,
  EnsRequestorModal,
  PlaceOfLoadingModal,
  PlaceOfUnloadingModal,
  RoutingModal,
} from '../Modals/TransportAndJourneyModals';

import { Button } from '@mui/material';

import { useStepper } from '../../../components/Stepper';

import { GBENSContext } from '../GBENSContainer';

const TransportAndJourney = () => {
  const [ensRequestorModalOpen, setEnsRequestorModalOpen] = useState(false);
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [placeOfLoadingModalOpen, setPlaceOfLoadingModalOpen] = useState(false);
  const [placeOfUnloadingModalOpen, setPlaceOfUnloadingModalOpen] = useState(false);
  const [routingModalOpen, setRoutingModalOpen] = useState(false);

  const { gotoStep } = useStepper();

  const {
    navToConsignmentId,
    setNavToConsignmentId,
    referenceData,
    data,
    updateData,
    handleShowWarningModal,
    isAccompanied,
    addNewConsignment,
    editConsignment,
    hasConsignments,
    isReadOnly,
  } = useContext(GBENSContext);

  const screen = 'TransportAndJourney';

  //Navigate to consignment if navigating to consignment from dashboard
  useEffect(() => {
    if (navToConsignmentId && hasConsignments) {
      editConsignment(navToConsignmentId);
      setNavToConsignmentId(false);
      gotoStep('UpdatingConsignment');
    }
  }, [navToConsignmentId, hasConsignments]);

  const onNext = async () => {
    if (isReadOnly) {
      gotoStep('Consignments');
    } else {
      handleShowWarningModal(screen, () => {
        if (hasConsignments) {
          gotoStep('Consignments');
        } else {
          addNewConsignment();
          gotoStep('AddingConsignment');
        }
      });
    }
  };

  const routeDisplayText = useMemo(() => {
    if (!data[screen]?.Routing) {
      const stopKeys = [
        'departureCountry',
        'transCountry1',
        'transCountry2',
        'transCountry3',
        'transCountry4',
        'transCountry5',
        'transCountry6',
        'transCountry7',
      ];
      const stopValues = stopKeys.map((s) => data[screen]?.Route?.[s]);
      const anyStops = stopValues.filter((s) => !!s && s !== -1);
      if (!anyStops || anyStops.length === 0) return null;
      const resolvedStops = anyStops.map((s) => referenceData.countryOfOriginMap?.find((c) => c.key === s)?.value);
      return (
        resolvedStops.join(' > ') +
        ' > ' +
        referenceData.countryOfOriginMap?.find((c) => c.key === data[screen]?.Route?.destinationCountry)?.value
      );
    }
  }, [data, referenceData]);

  const arrivalDateTooltip = useMemo(() => {
    // if mode of transport is Eurotunnel
    if (parseInt(data?.TransportAndJourney?.Vehicle?.ModeOfTransport) === 3) {
      return 'This is the arrival time at EuroTunnel FR (CET)';
    }
    return 'This is the arrival time in Great Britain (GMT)';
  }, [data]);

  const modeOfTransportChanged = (value) => {
    if (parseInt(value) === 3) {
      // find port for channel tunnel
      const channelTunnelPort = referenceData?.entryPorts?.find((port) => port?.value?.includes('Channel Tunnel'));
      if (channelTunnelPort?.key) {
        updateData({ target: { value: channelTunnelPort?.key } }, 'TransportAndJourney', 'portOfArrival');
      }
    }
  };

  return (
    <>
      <VehicleModal
        screen={screen}
        open={vehicleModalOpen}
        setOpen={setVehicleModalOpen}
        onTransportChanged={modeOfTransportChanged}
      />
      <PlaceOfLoadingModal screen={screen} open={placeOfLoadingModalOpen} setOpen={setPlaceOfLoadingModalOpen} />
      <PlaceOfUnloadingModal screen={screen} open={placeOfUnloadingModalOpen} setOpen={setPlaceOfUnloadingModalOpen} />
      <EnsRequestorModal screen={screen} open={ensRequestorModalOpen} setOpen={setEnsRequestorModalOpen} />
      <RoutingModal screen={screen} open={routingModalOpen} setOpen={setRoutingModalOpen} />

      <div style={{ backgroundColor: '#F6F6F6', padding: 30 }}>
        <KebabInput
          name="ENS Requestor*"
          value={`${data[screen]?.ENSRequestor?.['companyName']} (${data[screen].ENSRequestor['EORI']})`}
          onClick={() => setEnsRequestorModalOpen(true)}
          id="ENSRequestor"
          screen={screen}
          disabled={isReadOnly}
        />

        <SimpleTextInput
          name="Job Reference*"
          screen={screen}
          id="jobReference"
          placeholder="Enter your job or shipment reference"
          maxLength={35}
          disabled={isReadOnly}
        />

        <KebabInput
          name="Vehicle Identity*"
          value={
            data[screen]?.Vehicle?.vehicleID
              ? data[screen]?.Vehicle?.trailerNo
                ? `${data[screen]?.Vehicle?.vehicleID} - ${data[screen]?.Vehicle?.trailerNo}`
                : data[screen]?.Vehicle?.vehicleID
              : null
          }
          placeholder="Click link to add"
          linkText="Add Transport"
          onClick={() => setVehicleModalOpen(true)}
          id="Vehicle"
          screen={screen}
          disabled={isReadOnly}
        />

        <SimpleTextInput
          toolTip="Only required for unaccompanied shipment."
          name="Carrier/Ferry Operator"
          screen={screen}
          id="carrierFerryOperator"
          disabled={isAccompanied || isReadOnly}
          placeholder={isAccompanied ? 'Not required for Accompanied Shipment' : "Enter the Carrier's GB EORI"}
        />

        <SimpleSelect
          name="Port of Departure*"
          screen={screen}
          id="portOfDeparture"
          options={[...(referenceData.exitPorts || [])].sort((a, b) => a.value.localeCompare(b.value))}
          placeholder="-- Select --"
          disabled={isReadOnly}
        />

        <DatePickerComponent name="Departure Date*" screen={screen} id="departure" disabled={isReadOnly} />

        <SimpleSelect
          name="Port of Arrival*"
          screen={screen}
          id="portOfArrival"
          options={referenceData.entryPorts}
          placeholder="-- Select --"
          disabled={isReadOnly}
        />

        <DateTimePickerComponent
          name="Arrival Date and Time*"
          screen={screen}
          id="arrival"
          toolTip={arrivalDateTooltip}
          disabled={isReadOnly}
        />

        <KebabInput
          name="Single Place of Loading"
          value={
            data[screen]?.PlaceOfLoading?.placeName && data[screen]?.PlaceOfLoading?.placeName?.trim?.()?.length > 0
              ? data[screen]?.PlaceOfLoading?.placeName
              : ''
          }
          placeholder="Click link to add"
          toolTip={'Enter the location if all the goods are loaded at a single location.'}
          screen={screen}
          id="PlaceOfLoading"
          linkText="Place of Loading"
          onClick={() => setPlaceOfLoadingModalOpen(true)}
          disabled={isReadOnly}
        />

        <KebabInput
          name="Single Place of Unloading"
          value={
            data[screen]?.PlaceOfUnloading?.placeName && data[screen]?.PlaceOfUnloading?.placeName?.trim?.()?.length > 0
              ? data[screen]?.PlaceOfUnloading?.placeName
              : ''
          }
          placeholder="Click link to add"
          toolTip={'Enter the location if all the goods are unloaded at a single location.'}
          screen={screen}
          id="PlaceOfUnloading"
          linkText="Place of Unloading"
          onClick={() => setPlaceOfUnloadingModalOpen(true)}
          disabled={isReadOnly}
        />

        <KebabInput
          name="Route*"
          toolTip="Enter up to 9 routing countries."
          value={routeDisplayText}
          placeholder="Click link to add"
          id="Route"
          screen={screen}
          linkText="Routing Countries"
          onClick={() => setRoutingModalOpen(true)}
          disabled={isReadOnly}
        />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingBottom: 30,
          paddingLeft: 30,
          paddingRight: 30,
          paddingTop: 30,
        }}
      >
        <div></div>
        <Button variant="outlined" onClick={onNext}>
          Next: Consignments
        </Button>
      </div>
    </>
  );
};

export default TransportAndJourney;
